import { useState } from 'react';
import { Stack } from '@mui/material';
import {
  ExpandableContentButton,
  ExpandableContentWrapper,
} from 'components/shared';
import { CanvasGroupLinkButton } from 'components/canvas/groups';
import {
  VolSkewChartSettings,
  VolSkewZoomOutButton,
  VolSkewDownloadButton,
  VolSkewProvider,
} from 'components/implied_vol/VolSkew';
import VolSkewChart from 'components/implied_vol/VolSkew/VolSkewChart';
import { CanvasComponentConfig, CanvasContentId } from 'types/canvas';
import { DEFAULT_VOL_SKEW_CONFIG, VolSkewConfig } from 'types';
import { useRecoilValue } from 'recoil';
import { hiroSymbolsState } from 'states/hiro';
import CanvasSymSelector from './CanvasSymSelector';

interface CanvasVolSkewProps {
  id: number;
  sym: string;
  config: CanvasComponentConfig;
  onSymChange: (sym: string) => void;
  onConfigChange: (patch: Partial<CanvasComponentConfig>) => void;
}

const CanvasVolSkew = ({
  id,
  sym,
  config: initialConfig,
  onSymChange,
  onConfigChange,
}: CanvasVolSkewProps) => {
  const hiroSymbols = useRecoilValue(hiroSymbolsState);
  const symOptions = Array.from(hiroSymbols)
    .sort()
    .map((s) => ({ symbol: s }));
  const [config, setConfig] = useState<VolSkewConfig>(() => ({
    ...DEFAULT_VOL_SKEW_CONFIG,
    ...(initialConfig as Partial<VolSkewConfig>),
  }));

  const handleConfigChange = (patch: Partial<VolSkewConfig>) => {
    setConfig((prev) => ({ ...prev, ...patch }));
    onConfigChange(patch as Partial<CanvasComponentConfig>);
  };

  const expandableContentButtonType = `${CanvasContentId.VOL_SKEW}-${id}`;

  return (
    <ExpandableContentWrapper
      type={expandableContentButtonType}
      styleOverrides={{ p: 1, height: '100%' }}
    >
      <VolSkewProvider
        selectedSym={sym}
        config={config}
        onConfigChange={handleConfigChange}
      >
        <Stack
          direction="column"
          spacing={1}
          sx={{ height: '100%', width: '100%' }}
        >
          <Stack
            sx={{
              px: 1,
              py: 0.5,
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'flex-end',
            }}
          >
            <CanvasSymSelector
              sym={sym}
              options={symOptions}
              onSymChange={onSymChange}
            />
            <VolSkewZoomOutButton />
            <VolSkewDownloadButton />
            <VolSkewChartSettings />
            <CanvasGroupLinkButton
              componentId={id}
              contentId={CanvasContentId.VOL_SKEW}
            />
            <ExpandableContentButton type={expandableContentButtonType} />
          </Stack>
          <VolSkewChart />
        </Stack>
      </VolSkewProvider>
    </ExpandableContentWrapper>
  );
};

export default CanvasVolSkew;
